<template>
  <div v-if="currentUser" class="user-menu-shell" :class="`user-menu--${variant}`" data-tour="user-menu">
    <button
      v-if="showNotifications"
      class="btn btn-sm user-notification-button"
      data-tour="notifications-button"
      type="button"
      title="Notifications"
      aria-label="Notifications"
      @click="openNotifications"
    >
      <i class="fa fa-bell"></i>
      <span v-if="unreadCount > 0" class="user-notification-badge">
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <div
      ref="dropdownRoot"
      class="dropdown user-menu"
      @pointerenter="handleDropdownPointerEnter"
      @pointerleave="handleDropdownPointerLeave"
      @focusin="handleDropdownFocusIn"
      @focusout="handleDropdownFocusOut"
    >
      <button
        ref="dropdownToggle"
        class="btn btn-sm dropdown-toggle user-menu-button"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        @click="handleDropdownClick"
      >
        <span class="user-menu-avatar" aria-hidden="true">
          <img
            v-if="profilePhotoUrl"
            class="user-menu-avatar-image"
            :src="profilePhotoUrl"
            alt=""
            @error="handleAvatarError"
          />
          <i v-else class="fa fa-user"></i>
        </span>
        <span class="user-menu-copy">
          <span class="user-menu-name">{{ userName }}</span>
          <small v-if="subtitle" class="user-menu-subtitle">{{ subtitle }}</small>
        </span>
      </button>

      <ul class="dropdown-menu dropdown-menu-end user-menu-dropdown">
        <li>
          <button class="dropdown-item" type="button" @click="openProfile">
            <i class="fa fa-id-card me-2"></i>
            Mon profil
          </button>
        </li>
        <li>
          <router-link class="dropdown-item" :to="{ name: manualRouteName }">
            <i class="fa fa-book-open me-2"></i>
            Manuel d’utilisation
          </router-link>
        </li>
        <li v-if="showSettings">
          <button class="dropdown-item" type="button" @click="openSettings">
            <i class="fa fa-gear me-2"></i>
            Paramètres
          </button>
        </li>
        <li v-if="canEnableWebNotifications">
          <button class="dropdown-item" type="button" @click="enableWebNotifications">
            <i class="fa fa-bell me-2"></i>
            Activer les notifications web
          </button>
        </li>
        <li><hr class="dropdown-divider" /></li>
        <li>
          <button class="dropdown-item text-danger" type="button" @click="handleLogout">
            <i class="fa fa-arrow-right-from-bracket me-2"></i>
            Se déconnecter
          </button>
        </li>
      </ul>
    </div>

    <NotificationsDialog v-model:visible="notificationsDialogVisible" />
    <UserProfileDialog v-model:visible="profileDialogVisible" />
    <UserSettingsDialog
      v-model:visible="settingsDialogVisible"
      :show-appearance-settings="showAppearanceSettings"
      :cashier-mode="variant === 'cashier'"
    />
  </div>
</template>

<script>
import { Dropdown as BootstrapDropdown } from 'bootstrap';
import NotificationsDialog from '@/components/NotificationsDialog.vue';
import UserSettingsDialog from '@/components/UserSettingsDialog.vue';
import UserProfileDialog from '@/components/UserProfileDialog.vue';
import webNotifications from '@/services/webNotifications';

const DROPDOWN_CLOSE_DELAY = 150;
const DROPDOWN_HOVER_QUERY = '(hover: hover) and (pointer: fine)';

export default {
  name: 'UserMenu',
  components: {
    NotificationsDialog,
    UserSettingsDialog,
    UserProfileDialog,
  },
  props: {
    variant: {
      type: String,
      default: 'dark',
    },
    showRole: {
      type: Boolean,
      default: true,
    },
    showShop: {
      type: Boolean,
      default: true,
    },
    showNotifications: {
      type: Boolean,
      default: true,
    },
    showSettings: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      notificationsDialogVisible: false,
      profileDialogVisible: false,
      settingsDialogVisible: false,
      avatarImageFailed: false,
      unreadCount: 0,
      unreadTimer: null,
      dropdownInstance: null,
      dropdownCloseTimer: null,
      webNotificationPermission: webNotifications.permission(),
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    shopName() {
      return this.$store.getters['shop/shopName'];
    },
    userName() {
      return this.currentUser?.name || this.currentUser?.username || 'Utilisateur';
    },
    rawProfilePhotoUrl() {
      return this.currentUser?.profile_photo_url || '';
    },
    profilePhotoUrl() {
      if (this.avatarImageFailed) {
        return '';
      }

      return this.rawProfilePhotoUrl;
    },
    roleLabel() {
      const role = this.currentUser?.role;
      if (role === 'gerant') return 'Gérant';
      if (role === 'caissier') return 'Caissier';
      if (role === 'support') return 'Support';
      if (role === 'demo') return 'Démo';
      return role || '';
    },
    subtitle() {
      const parts = [];

      if (this.showRole && this.roleLabel) {
        parts.push(this.roleLabel);
      }

      if (this.showShop && this.shopName) {
        parts.push(this.shopName);
      }

      return parts.join(' · ');
    },
    canEnableWebNotifications() {
      return this.showNotifications
        && this.webNotificationPermission !== 'unsupported'
        && this.webNotificationPermission !== 'granted';
    },
    manualRouteName() {
      const cashierRoutes = [
        'CashierSales',
        'CashierInventories',
        'CashierInventoryCount',
        'CashierEndOfDay',
        'CashierManual',
      ];

      if (this.currentUser?.role === 'caissier' || cashierRoutes.includes(String(this.$route.name || ''))) {
        return 'CashierManual';
      }

      return 'AdminManual';
    },
    showAppearanceSettings() {
      return this.variant !== 'cashier';
    },
  },
  mounted() {
    this.initializeDropdown();
    this.fetchUnreadCount();
    this.startWebNotificationsIfAllowed();
    this.unreadTimer = window.setInterval(this.fetchUnreadCount, 60000);
    window.addEventListener('suite241:notifications-changed', this.fetchUnreadCount);
  },
  beforeUnmount() {
    this.clearDropdownCloseTimer();
    this.dropdownInstance?.dispose();
    this.dropdownInstance = null;

    if (this.unreadTimer) {
      window.clearInterval(this.unreadTimer);
    }
    window.removeEventListener('suite241:notifications-changed', this.fetchUnreadCount);
  },
  watch: {
    rawProfilePhotoUrl() {
      this.avatarImageFailed = false;
    },
    '$route.name'() {
      this.fetchUnreadCount();
    },
  },
  methods: {
    initializeDropdown() {
      if (!this.dropdownInstance && this.$refs.dropdownToggle) {
        this.dropdownInstance = BootstrapDropdown.getOrCreateInstance(this.$refs.dropdownToggle);
      }

      return this.dropdownInstance;
    },

    supportsDropdownHover() {
      return typeof window.matchMedia === 'function'
        && window.matchMedia(DROPDOWN_HOVER_QUERY).matches;
    },

    clearDropdownCloseTimer() {
      if (this.dropdownCloseTimer !== null) {
        window.clearTimeout(this.dropdownCloseTimer);
        this.dropdownCloseTimer = null;
      }
    },

    eventLeavesDropdown(event) {
      const dropdownRoot = this.$refs.dropdownRoot;
      return !dropdownRoot || !event.relatedTarget || !dropdownRoot.contains(event.relatedTarget);
    },

    showDropdownFromHover() {
      if (!this.supportsDropdownHover()) return;

      this.clearDropdownCloseTimer();
      this.initializeDropdown()?.show();
    },

    scheduleDropdownClose() {
      this.clearDropdownCloseTimer();
      this.dropdownCloseTimer = window.setTimeout(() => {
        this.dropdownCloseTimer = null;
        this.dropdownInstance?.hide();
      }, DROPDOWN_CLOSE_DELAY);
    },

    handleDropdownClick() {
      // Bootstrap's data API already toggles the menu during event capture.
      this.clearDropdownCloseTimer();
    },

    handleDropdownPointerEnter() {
      this.showDropdownFromHover();
    },

    handleDropdownPointerLeave(event) {
      if (!this.supportsDropdownHover() || !this.eventLeavesDropdown(event)) return;

      this.scheduleDropdownClose();
    },

    handleDropdownFocusIn() {
      this.showDropdownFromHover();
    },

    handleDropdownFocusOut(event) {
      if (!this.supportsDropdownHover() || !this.eventLeavesDropdown(event)) return;

      this.scheduleDropdownClose();
    },

    handleAvatarError() {
      this.avatarImageFailed = true;
    },

    openNotifications() {
      this.notificationsDialogVisible = true;
    },

    openProfile() {
      this.profileDialogVisible = true;
    },

    openSettings() {
      this.settingsDialogVisible = true;
    },

    async startWebNotificationsIfAllowed() {
      this.webNotificationPermission = webNotifications.permission();

      if (this.webNotificationPermission === 'granted') {
        await webNotifications.start().catch(() => undefined);
      }
    },

    async enableWebNotifications() {
      const enabled = await webNotifications.enable().catch(() => false);
      this.webNotificationPermission = webNotifications.permission();

      if (enabled) {
        this.fetchUnreadCount();
      }
    },

    async fetchUnreadCount() {
      if (!this.currentUser) return;

      try {
        const response = await this.$api.get('/notifications/unread-count', { skipErrorToast: true });
        const data = response.data.data || response.data || {};
        this.unreadCount = Number(data.unread_count || 0);
      } catch (error) {
        this.unreadCount = 0;
      }
    },

    async handleLogout() {
      try {
        webNotifications.stop();
        await this.$store.dispatch('auth/logout');
      } finally {
        this.$router.push({ name: 'Login' });
      }
    },
  },
};
</script>

<style scoped>
.user-menu-shell {
  align-items: center;
  display: inline-flex;
  gap: 0.5rem;
  min-width: 0;
}

.user-menu {
  min-width: 0;
}

.user-notification-button {
  align-items: center;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  position: relative;
  width: 34px;
}

.user-notification-button .fa-bell {
  font-size: 1.15rem;
  transition: transform 0.18s ease;
}

.user-menu--dark .user-notification-button,
.user-menu--cashier .user-notification-button {
  color: #ffffff;
}

.user-menu--dark .user-notification-button:hover,
.user-menu--dark .user-notification-button:focus,
.user-menu--cashier .user-notification-button:hover,
.user-menu--cashier .user-notification-button:focus {
  background-color: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  transform: translateY(-2px);
}

.user-menu--dark .user-notification-button:hover .fa-bell,
.user-menu--dark .user-notification-button:focus-visible .fa-bell,
.user-menu--cashier .user-notification-button:hover .fa-bell,
.user-menu--cashier .user-notification-button:focus-visible .fa-bell {
  animation: notification-bell-ring 0.42s ease-in-out;
}

.user-menu--light .user-notification-button {
  color: #0f172a;
}

.user-notification-badge {
  align-items: center;
  background-color: #dc3545;
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.62rem;
  font-weight: 700;
  height: 17px;
  justify-content: center;
  min-width: 17px;
  padding-inline: 4px;
  position: absolute;
  right: -4px;
  top: -4px;
}

.user-menu-button {
  align-items: center;
  border: 0;
  border-radius: 999px;
  display: inline-flex;
  gap: 0.5rem;
  min-height: 34px;
  max-width: min(260px, 56vw);
  padding: 0.28rem 0.75rem 0.28rem 0.32rem;
}

.user-menu--dark .user-menu-button,
.user-menu--cashier .user-menu-button {
  color: #ffffff;
}

.user-menu--dark .user-menu-button:hover,
.user-menu--dark .user-menu-button:focus,
.user-menu--cashier .user-menu-button:hover,
.user-menu--cashier .user-menu-button:focus {
  background-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.16rem rgba(255, 255, 255, 0.24);
  color: #ffffff;
}

.user-menu--light .user-menu-button {
  color: #0f172a;
}

@keyframes notification-bell-ring {
  0%, 100% { transform: rotate(0); }
  25% { transform: rotate(-14deg); }
  50% { transform: rotate(12deg); }
  75% { transform: rotate(-8deg); }
}

@media (prefers-reduced-motion: reduce) {
  .user-notification-button,
  .user-notification-button .fa-bell {
    transition: none;
  }

  .user-menu--dark .user-notification-button:hover .fa-bell,
  .user-menu--dark .user-notification-button:focus-visible .fa-bell,
  .user-menu--cashier .user-notification-button:hover .fa-bell,
  .user-menu--cashier .user-notification-button:focus-visible .fa-bell {
    animation: none;
  }
}

.user-menu-avatar {
  align-items: center;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  display: inline-flex;
  flex: 0 0 26px;
  height: 26px;
  justify-content: center;
  overflow: hidden;
  width: 26px;
}

.user-menu-avatar-image {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.user-menu--light .user-menu-avatar {
  background-color: rgba(13, 110, 253, 0.12);
  color: var(--bs-primary);
}

.user-menu-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.05;
  min-width: 0;
  text-align: left;
}

.user-menu-name,
.user-menu-subtitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu-name {
  font-size: 0.78rem;
  font-weight: 700;
}

.user-menu-subtitle {
  font-size: 0.65rem;
  opacity: 0.78;
}

.user-menu-dropdown {
  border: 0;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.18);
  min-width: 210px;
}
</style>
